import { useEffect, useState } from 'react';
import {
  Compass, LayoutDashboard, UserPlus, ClipboardList, Sparkles, ListChecks,
  Clock, FileText, FileStack, CheckCircle2, ArrowRight, ArrowLeft,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// CAD Cleanup SS10: a brief "Take a Tour" overview of the main pages, mirroring the
// CRM welcome tour (a step-through modal). Opened from the Take a Tour nav button.
interface TourStep {
  icon: React.ElementType;
  title: string;
  body: React.ReactNode;
  to?: string;        // optional "open this page" jump
  toLabel?: string;
  admin?: boolean;    // step shown only to admins
}

const STEPS: TourStep[] = [
  {
    icon: Compass,
    title: 'Welcome to BRC Client Administration',
    body: 'A quick tour of the main screens. Use Next to step through, or Skip anytime — you can reopen this from Take a Tour in the menu.',
  },
  {
    icon: LayoutDashboard,
    title: 'Dashboard',
    body: 'Your at-a-glance snapshot: total clients and engagements, requests submitted through the app, pending and completed counts, and average turnaround.',
    to: '/', toLabel: 'Open Dashboard',
  },
  {
    icon: UserPlus,
    title: 'New Client',
    body: 'Add a new client or sub-client — details, tax IDs, manager, and entity info. Submitting creates a client request.',
    to: '/clients/new', toLabel: 'Open New Client',
  },
  {
    icon: ClipboardList,
    title: 'View / Change Clients',
    body: 'Search existing clients and open one to view or change its details.',
    to: '/clients', toLabel: 'Open View / Change Clients',
  },
  {
    icon: Sparkles,
    title: 'New Engagement',
    body: 'Create an engagement for a client. Pick the service and template; the form shows only the fields that engagement type needs.',
    to: '/engagements/new', toLabel: 'Open New Engagement',
  },
  {
    icon: ListChecks,
    title: 'View / Change Engagements',
    body: 'Search engagements and open one to view or edit its details.',
    to: '/engagements', toLabel: 'Open View / Change Engagements',
  },
  {
    icon: FileStack,
    title: 'Mass Change Request',
    body: 'Change many clients or engagements at once: download a template with their current details, edit it in Excel, and upload it. The file is sent as one request, processed in Maconomy, and you get an email when it is completed.',
    to: '/forms/mass-change', toLabel: 'Open Mass Change Request',
  },
  {
    icon: Clock,
    title: 'Requests',
    body: 'Pending Requests, Downloaded Requests, and Completed Requests track client and engagement requests through Maconomy onboarding.',
    to: '/pending', toLabel: 'Open Pending Requests', admin: true,
  },
  {
    icon: FileText,
    title: 'Forms',
    body: 'Submit Client Termination, Engagement Discontinuation, and Client Reactivation requests. Admins track the checklist for each request under Forms > Submitted Forms.',
    to: '/forms/termination', toLabel: 'Open Forms',
  },
  {
    icon: CheckCircle2,
    title: "You're all set",
    body: 'Reopen this tour anytime from Take a Tour in the left menu.',
  },
];

export function WelcomeTour({ open, onClose, isAdmin }: { open: boolean; onClose: () => void; isAdmin: boolean }) {
  const navigate = useNavigate();
  const steps = STEPS.filter((s) => !s.admin || isAdmin);
  const [step, setStep] = useState(0);
  useEffect(() => { if (open) setStep(0); }, [open]);
  if (!open) return null;

  const idx = Math.min(step, steps.length - 1);
  const current = steps[idx];
  const isLast = idx === steps.length - 1;
  const Icon = current.icon;
  const go = (to: string) => { navigate(to); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-card p-6 shadow-xl">
        <div className="flex flex-col items-center text-center">
          <div className="brc-gradient mb-4 flex h-14 w-14 items-center justify-center rounded-2xl">
            <Icon className="h-7 w-7 text-white" />
          </div>
          <h2 className="text-xl font-bold text-foreground">{current.title}</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{current.body}</p>
          {current.to && (
            <button type="button" onClick={() => go(current.to!)}
              className="mt-3 inline-flex items-center gap-1 text-sm text-primary hover:underline">
              {current.toLabel ?? 'Show me'} <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="mt-5 flex items-center justify-center gap-1.5">
          {steps.map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-5 bg-primary' : 'w-1.5 bg-border'}`} />
          ))}
        </div>

        <div className="mt-5 flex items-center justify-between">
          <button type="button" onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground">Skip</button>
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <button type="button" onClick={() => setStep(idx - 1)} className="btn-secondary inline-flex items-center gap-1 text-sm">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
            )}
            {isLast ? (
              <button type="button" onClick={onClose} className="btn-success text-sm">Get started</button>
            ) : (
              <button type="button" onClick={() => setStep(idx + 1)} className="btn-primary inline-flex items-center gap-1 text-sm">
                Next <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        <p className="mt-3 text-center text-xs text-muted-foreground">Step {idx + 1} of {steps.length}</p>
      </div>
    </div>
  );
}
